import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Button from '../Button';
import { useSidebar } from '../../../contexts/SidebarContext';

/**
 * Shopify-style sticky save bar for dirty edit forms.
 * Portaled to document.body; offsets for admin/vendor sidebar width.
 */
const StickySaveBar = ({
  open = false,
  message = 'Unsaved changes',
  onDiscard,
  onSave,
  saving = false,
  saveLabel = 'Save',
  discardLabel = 'Discard',
  saveDisabled = false,
}) => {
  const location = useLocation();
  const {
    isVendorSidebarCollapsed,
    isAdminSidebarCollapsed,
  } = useSidebar();

  if (!open || typeof document === 'undefined') return null;

  const isAdmin = location.pathname.startsWith('/admin');
  const collapsed = isAdmin ? isAdminSidebarCollapsed : isVendorSidebarCollapsed;

  return createPortal(
    <div
      className={`theme-vendalist product-sticky-save ${
        collapsed ? 'is-sidebar-collapsed' : 'is-sidebar-expanded'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="product-sticky-save-inner">
        <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-ink">
          {message}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {onDiscard ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onDiscard}
              disabled={saving}
            >
              {discardLabel}
            </Button>
          ) : null}
          {onSave ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onSave}
              disabled={saving || saveDisabled}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Saving…
                </>
              ) : (
                saveLabel
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StickySaveBar;
